import React, { useMemo } from 'react'
import { moneyOrBlank } from './overview'

// ─────────────────────────────────────────────────────────────────────────────
//   Consolidado por categoría — dos cifras que no son la misma cifra
//
//   Cada fila trae `cash_amount` (lo que salió de la cuenta) y `expense_amount`
//   (lo que es coste). Son iguales en casi todas, y distintas en dos:
//
//     debt_service → el principal amortizado reduce un pasivo y no cuesta nada
//     founder      → SUPPORT_USE es un retiro contra patrimonio, no un coste
//
//   Las dos columnas se pintan SIEMPRE, no sólo cuando difieren, y esa es una
//   decisión y no una comodidad: el backend devuelve las dos precisamente
//   porque no existe "el" número, y elegir una en el cliente haría la otra
//   incontestable desde la pantalla. Sumando cualquiera de las dos columnas se
//   obtiene una respuesta correcta a ESA pregunta; sumando una mezcla no se
//   obtiene respuesta a ninguna. Con las dos siempre a la vista, la diferencia
//   es estructural en vez de aparecer y desaparecer entre meses.
//
//   Donde difieren viene un `note` explicando por qué, y va impreso bajo la
//   fila — no en un `title`. Un tooltip es invisible en móvil, invisible al
//   imprimir e invisible para quien no sabe que hay algo que mirar, que es
//   exactamente quien necesita leerlo.
//
// ─────────────────────────────────────────────────────────────────────────────
//   Sobre un rango, y ordenada por lo que más cuesta
//
//   `expense.by_category` YA es el acumulado de la ventana entera: el backend
//   filtra `entry_date` entre los dos extremos y agrega por categoría en una
//   sola pasada, así que cada fila es el total del rango. Esta pantalla no suma
//   nada — no hay un segundo bloque de "totales por categoría" porque sería
//   esta misma tabla pintada dos veces.
//
//   Lo único que se decide aquí es el ORDEN. El backend devuelve por
//   `sort_order`, que es el orden del plan de cuentas y sirve para leer la
//   estructura contable; ordenarlas por `expense_amount` descendente contesta
//   otra pregunta, la que trae a alguien a esta tabla: dónde se va el dinero.
//   Es reordenar filas, no recalcular ninguna: los importes son los que llegan.
//
//   El desempate es `cash_amount`, y existe por una fila concreta. Founder
//   support tiene `expense_amount` 0 —un retiro contra patrimonio no es un
//   coste— con un `cash_amount` que puede ser el mayor de la tabla. Sin
//   desempate se hundiría entre los ceros en un orden arbitrario y la salida de
//   caja más grande del rango sería la más difícil de encontrar.
//
//   Con `[...rows]`: `sort` ordena en sitio y el array es el de la respuesta.
// ─────────────────────────────────────────────────────────────────────────────

export default function ExpenseByCategory({ expense, rangeText, loading }) {
  const rows = useMemo(() => {
    const source = expense?.by_category || []
    return [...source].sort(
      (a, b) => (b.expense_amount - a.expense_amount) || (b.cash_amount - a.cash_amount),
    )
  }, [expense])

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      {/* Una línea. Dónde difieren las dos columnas lo dice la `note` de la
          propia fila, que es donde hace falta; repetirlo aquí en abstracto
          costaba cinco líneas por encima de la tabla. */}
      <h3 style={{ margin: 0, fontSize: 15 }}>Spend in {rangeText}</h3>
      <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.55 }}>
        <strong>Cash out</strong> left the account, <strong>expense</strong> is what it cost — never
        add one to the other. Sorted by expense, biggest first.
      </p>

      {loading ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 12 }}>Loading…</p>
      ) : rows.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 12 }}>
          No expenses recorded in {rangeText} yet.
        </p>
      ) : (
        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table>
            <thead>
              <tr>
                <th>Category</th>
                <th style={{ textAlign: 'right' }}>Cash out</th>
                <th style={{ textAlign: 'right' }}>Expense</th>
                <th style={{ textAlign: 'right', width: 90 }}>Entries</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const differs = row.cash_amount !== row.expense_amount
                return (
                  <React.Fragment key={row.category_id}>
                    <tr>
                      <td>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                          <span>{row.name}</span>
                          {/* `derived: true` significa asientos manuales bajo una
                              línea que `financial/` ya calcula: el mismo dólar en
                              los libros dos veces. El servicio lo impide, así que
                              sólo se llega escribiendo alrededor del write path.
                              El backend lo expone en vez de esconderlo para que
                              se encuentre, y esconderlo aquí anularía justo eso.
                              Se lee con `=== true` porque el campo puede no venir
                              todavía en un backend anterior a él, y un `undefined`
                              no es una anomalía: es una versión sin el flag. */}
                          {row.derived === true && (
                            <span
                              className="badge badge-red"
                              title="Manual entries filed under a line financial/ already computes — the same dollar counted twice"
                            >
                              derived
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {moneyOrBlank(row.cash_amount)}
                      </td>
                      <td
                        style={{
                          textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap',
                          color: differs ? 'var(--blue)' : 'var(--text-primary)',
                        }}
                      >
                        {moneyOrBlank(row.expense_amount)}
                      </td>
                      <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>{row.entries}</td>
                    </tr>
                    {row.note && (
                      <tr>
                        <td
                          colSpan={4}
                          style={{
                            paddingTop: 0, fontSize: 12, color: 'var(--text-secondary)',
                            lineHeight: 1.5, borderTop: 'none',
                          }}
                        >
                          {row.note}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
            <tfoot>
              <tr>
                <th>Total</th>
                {/* Los dos totales, cada uno bajo su columna y nunca sumados
                    entre sí. `cash_total` responde "cuánto dinero salió" y
                    `expense_total` "cuánto costó el mes"; un único "total"
                    aquí obligaría a elegir cuál de las dos preguntas deja de
                    tener respuesta. */}
                <th style={{ textAlign: 'right' }}>{moneyOrBlank(expense?.cash_total)}</th>
                <th style={{ textAlign: 'right' }}>{moneyOrBlank(expense?.expense_total)}</th>
                <th />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  )
}
